'use client';

import { ReactNode, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { Button } from '../Clickables/Buttons/Button';
import { OverflowDirection, useElementIsOverflowing } from '@hooks/useElementIsOverflowing';

type ClampBreakpoint = 'mobile' | 'tablet' | 'desktop';
type ClampLines = 2 | 3 | 4;

interface CollapsibleTextProps {
  children: ReactNode;
  maxLines?: ClampLines;
  className?: string;
  // Which breakpoints to clamp on. Default: all three.
  // Tailwind breakpoints: mobile = below sm, tablet = sm to md-1, desktop = md+.
  clampOn?: ClampBreakpoint[];
  showMoreLabel?: string;
  showLessLabel?: string;
  // 'div' (default) for block / rich-text content. 'span' for inline use
  // inside a <p>/<Text> where the truncated body flows after other content.
  // Span mode skips the bottom fade since vertical fades don't make sense
  // for inline text.
  as?: 'span' | 'div';
}

// Literal class strings — Tailwind's content scanner can't resolve template
// literals, so the full classes need to appear in source. See
// https://tailwindcss.com/docs/detecting-classes-in-source-files
const MOBILE_CLAMP: Record<ClampLines, string> = {
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
};
const TABLET_CLAMP: Record<ClampLines, string> = {
  2: 'sm:line-clamp-2',
  3: 'sm:line-clamp-3',
  4: 'sm:line-clamp-4',
};
const DESKTOP_CLAMP: Record<ClampLines, string> = {
  2: 'md:line-clamp-2',
  3: 'md:line-clamp-3',
  4: 'md:line-clamp-4',
};
const TABLET_UNCLAMP = 'sm:line-clamp-none';
const DESKTOP_UNCLAMP = 'md:line-clamp-none';

// Linear-gradient mask fading the bottom of the clamped text so the
// truncation reads as a soft fade instead of "…" + read more.
const MOBILE_FADE = 'mask-b-from-70% mask-b-to-100%';
const TABLET_FADE = 'sm:mask-b-from-70% sm:mask-b-to-100%';
const DESKTOP_FADE = 'md:mask-b-from-70% md:mask-b-to-100%';
const TABLET_UNFADE = 'sm:mask-none';
const DESKTOP_UNFADE = 'md:mask-none';

const DEFAULT_CLAMP_ON: ClampBreakpoint[] = ['mobile', 'tablet', 'desktop'];

// Build the clamp class for the given breakpoints. We only emit a class when
// the on/off state changes at a breakpoint boundary, so the output stays
// minimal.
const buildClampClass = (clampOn: ClampBreakpoint[], maxLines: ClampLines): string => {
  const onMobile = clampOn.includes('mobile');
  const onTablet = clampOn.includes('tablet');
  const onDesktop = clampOn.includes('desktop');
  const parts: string[] = [];
  if (onMobile) {
    parts.push(MOBILE_CLAMP[maxLines]);
  }
  if (onTablet && !onMobile) {
    parts.push(TABLET_CLAMP[maxLines]);
  } else if (!onTablet && onMobile) {
    parts.push(TABLET_UNCLAMP);
  }
  if (onDesktop && !onTablet) {
    parts.push(DESKTOP_CLAMP[maxLines]);
  } else if (!onDesktop && onTablet) {
    parts.push(DESKTOP_UNCLAMP);
  }
  return parts.join(' ');
};

// Same breakpoint logic as buildClampClass, but emits mask utilities so the
// fade overlay matches wherever the clamp is active.
const buildFadeClass = (clampOn: ClampBreakpoint[]): string => {
  const onMobile = clampOn.includes('mobile');
  const onTablet = clampOn.includes('tablet');
  const onDesktop = clampOn.includes('desktop');
  const parts: string[] = [];
  if (onMobile) {
    parts.push(MOBILE_FADE);
  }
  if (onTablet && !onMobile) {
    parts.push(TABLET_FADE);
  } else if (!onTablet && onMobile) {
    parts.push(TABLET_UNFADE);
  }
  if (onDesktop && !onTablet) {
    parts.push(DESKTOP_FADE);
  } else if (!onDesktop && onTablet) {
    parts.push(DESKTOP_UNFADE);
  }
  return parts.join(' ');
};

// Mirror the breakpoint set for button visibility — only show the toggle on
// breakpoints where the text is actually clamped.
const buildButtonClass = (clampOn: ClampBreakpoint[]): string => {
  const onMobile = clampOn.includes('mobile');
  const onTablet = clampOn.includes('tablet');
  const onDesktop = clampOn.includes('desktop');
  const parts: string[] = [onMobile ? 'inline-block' : 'hidden'];
  if (onTablet !== onMobile) {
    parts.push(onTablet ? 'sm:inline-block' : 'sm:hidden');
  }
  if (onDesktop !== onTablet) {
    parts.push(onDesktop ? 'md:inline-block' : 'md:hidden');
  }
  return parts.join(' ');
};

export const CollapsibleText = ({
  children,
  maxLines = 3,
  className,
  clampOn = DEFAULT_CLAMP_ON,
  showMoreLabel = 'read more',
  showLessLabel = 'show less',
  as: Wrapper = 'div',
}: CollapsibleTextProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const isBlock = Wrapper === 'div';
  const clampClass = isOpen ? '' : buildClampClass(clampOn, maxLines);
  const fadeClass = isOpen || !isBlock ? '' : buildFadeClass(clampOn);
  const buttonClass = buildButtonClass(clampOn);
  const outerClass = isBlock
    ? 'flex flex-col items-start gap-2'
    : 'text-left break-words whitespace-pre-wrap';

  const isOverflowing = useElementIsOverflowing(contentRef, OverflowDirection.Y, [clampClass], 1);
  const showButton = isOpen || isOverflowing;

  return (
    <Wrapper className={twMerge(outerClass, className)}>
      <Wrapper ref={contentRef} className={`${clampClass} ${fadeClass}`}>
        {children}
      </Wrapper>
      {showButton && (
        <Button
          size="xs"
          variant="textNone"
          onClick={() => setIsOpen(o => !o)}
          className={`text-trench font-heading text-xs font-medium ${buttonClass}`}
        >
          {isOpen ? showLessLabel : showMoreLabel}
        </Button>
      )}
    </Wrapper>
  );
};
